import { AnimaliaListingCardDTO } from '../../../../application/mappers/AnimaliaListingMapper';
import { buildRoute } from '../../../../../../shared/ui/router';
import { animaliaListingDesignTokens } from '../shared/animalia-listing-design-tokens';
import './card-media/mv-animalia-listing-card-media';
import './card-tags/mv-animalia-listing-card-tags';
import './card-excerpt/mv-animalia-listing-card-excerpt';
import { MvAnimaliaListingCardMedia } from './card-media/mv-animalia-listing-card-media';
import { MvAnimaliaListingCardTags } from './card-tags/mv-animalia-listing-card-tags';
import { MvAnimaliaListingCardExcerpt } from './card-excerpt/mv-animalia-listing-card-excerpt';

export class MvAnimaliaListingCard extends HTMLElement {
  private item: AnimaliaListingCardDTO | null = null;

  private detailQuery: Readonly<Record<string, string>> = {};

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setItem(item: AnimaliaListingCardDTO): void {
    this.item = item;
    this.render();
    this.attachChildren();
  }

  setDetailQuery(detailQuery: Readonly<Record<string, string>>): void {
    this.detailQuery = { ...detailQuery };
    this.render();
    this.attachChildren();
  }

  connectedCallback(): void {
    this.render();
    this.attachChildren();
  }

  private attachChildren(): void {
    if (!this.shadowRoot || !this.item) {
      return;
    }

    const media = this.shadowRoot.querySelector('mv-animalia-listing-card-media') as
      | MvAnimaliaListingCardMedia
      | null;
    const tags = this.shadowRoot.querySelector('mv-animalia-listing-card-tags') as
      | MvAnimaliaListingCardTags
      | null;
    const excerpt = this.shadowRoot.querySelector('mv-animalia-listing-card-excerpt') as
      | MvAnimaliaListingCardExcerpt
      | null;

    media?.setMedia(
      this.item.positionLabel,
      this.item.mediaBadge,
      this.item.imageSrc,
      this.item.imageAlt,
      this.item.imageLicenseName,
      this.item.imageLicenseUrl,
      this.item.imageArtist,
      this.item.imageAttributionRequired,
    );
    tags?.setTags(this.item.tags);
    excerpt?.setContent(this.item.excerptTitle, this.item.excerptBody);
  }

  private render(): void {
    if (!this.shadowRoot || !this.item) {
      return;
    }

    const supportLinks = [
      this.item.wikipediaUrl
        ? `<a class="media-badge wiki-badge" href="${this.escapeAttribute(this.item.wikipediaUrl)}" target="_blank" rel="noreferrer">Wikipedia</a>`
        : '',
      `<a class="media-badge detail-badge" href="${this.escapeAttribute(this.buildDetailUrl())}">Abrir ficha</a>`,
    ].filter((link) => link !== '');

    this.shadowRoot.innerHTML = `
      <style>
        ${animaliaListingDesignTokens}

        :host {
          display: block;
          height: 100%;
        }

        article {
          display: flex;
          flex-direction: column;
          height: 100%;
          border-radius: 24px;
          background: rgba(250, 247, 241, 0.92);
          border: 1px solid rgba(49, 70, 61, 0.12);
          overflow: hidden;
          box-shadow: 0 18px 38px rgba(31, 42, 35, 0.1);
          transition: transform 180ms ease, box-shadow 180ms ease;
        }

        article:hover {
          transform: translateY(-4px);
          box-shadow: 0 22px 44px rgba(31, 42, 35, 0.14);
        }

        .species-body {
          display: flex;
          flex: 1;
          flex-direction: column;
          padding: 22px;
        }

        h2 {
          margin: 8px 0 0;
          font-size: 2rem;
          color: #31463d;
          font-family: Georgia, 'Times New Roman', serif;
          line-height: 1.02;
        }

        .common-name {
          margin: 10px 0 0;
          display: grid;
          gap: 4px;
          color: rgba(32, 39, 32, 0.64);
        }

        .common-name span {
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-size: 0.73rem;
          color: #5a6d62;
        }

        .support-links {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: auto;
          padding-top: 14px;
        }

        .media-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 999px;
          background: rgba(95, 120, 128, 0.16);
          color: #31463d;
          border: 1px solid rgba(95, 120, 128, 0.2);
          text-decoration: none;
        }

        .detail-badge {
          margin-left: auto;
          background: #31463d;
          border-color: #31463d;
          color: #f7f3ed;
        }

        @media (max-width: 720px) {
          .species-body {
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      </style>

      <article class="species-card">
        <mv-animalia-listing-card-media></mv-animalia-listing-card-media>
        <div class="species-body">
          <div class="species-heading">
            <p class="field-label">Nombre cientifico</p>
            <h2>${this.escapeHtml(this.item.scientificName)}</h2>
            <p class="common-name"><span>Nombre comun</span>${this.escapeHtml(this.item.commonName)}</p>
          </div>
          <mv-animalia-listing-card-tags></mv-animalia-listing-card-tags>
          <mv-animalia-listing-card-excerpt></mv-animalia-listing-card-excerpt>
          ${supportLinks.length > 0 ? `<div class="support-links">${supportLinks.join('')}</div>` : ''}
        </div>
      </article>
    `;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }

  private buildDetailUrl(): string {
    if (!this.item) {
      return '/';
    }

    return buildRoute(`/animalia/${encodeURIComponent(this.item.slug)}`, this.detailQuery);
  }
}

if (!customElements.get('mv-animalia-listing-card')) {
  customElements.define('mv-animalia-listing-card', MvAnimaliaListingCard);
}
